import { SELECTION_WORD_LIST } from "../../../config/selection-paths";
import {
  CART_OVERVIEW_URL,
  CART_SIGNUP_URL,
} from "@/common/static/checkout/checkout.static";

type PathNameToPageTypeMapEntry = {
  pathName: string | string[];
  pageType: string;
  exact?: boolean;
};

/**
 * Array containing maps between first component of `window.location.pathname`
 * and `page_type` Google Tag Manager parameter.
 *
 * NOTE: Last entry marked with `*` is a "catch-all" because of the way we "capture" urls that
 * should result in search page being shown.
 */
export const PATHNAME_PAGETYPE_MAP_ARRAY = [
  {
    pathName: "/",
    pageType: "home",
    exact: true,
  },
  {
    pathName: "/create-ad",
    pageType: "create_ad_flow",
  },
  // Open Home (10-27): its pages fell through to "search_results". Most specific first.
  {
    pathName: "/open-home/my-home",
    pageType: "open_home_my_home",
  },
  {
    pathName: "/open-home/rooms",
    pageType: "open_home_room",
  },
  {
    pathName: "/open-home/items",
    pageType: "open_home_item",
  },
  {
    pathName: "/open-home",
    pageType: "open_home_shop",
  },
  {
    pathName: [
      "/products",
      "/producten",
      "/produkte",
      "/produits",
      "/productos",
      "/prodotti",
    ],
    pageType: "pdp",
  },
  {
    pathName: "/account/login",
    pageType: "login",
  },
  {
    pathName: "/account/signup",
    pageType: "sign_up",
  },
  {
    pathName: "/account",
    pageType: "my_account",
  },
  {
    pathName: ["/collection", "/collections"],
    pageType: "collection",
  },
  {
    pathName: ["/brand", "/brands"],
    pageType: "brand",
  },
  {
    pathName: ["/icon", "/icons"],
    pageType: "icon",
  },
  {
    pathName: "/showroom",
    pageType: "showroom",
  },
  {
    pathName: "/similar",
    pageType: "similar",
  },
  {
    pathName: CART_SIGNUP_URL,
    pageType: "cart_signup",
  },
  {
    pathName: CART_OVERVIEW_URL,
    pageType: "cart_overview",
  },
  {
    pathName: ["/cart", "/cart2"],
    pageType: "cart",
  },
  {
    pathName: ["/checkout", "/checkout2"],
    pageType: "checkout",
  },
  {
    pathName: "/blog",
    pageType: "blog",
  },
  {
    pathName: [
      "/new",
      "/nieuw",
      "/neu",
      "/sale",
      "/most-popular",
      "/meest-populair",
      "/am-beliebtesten",
      "/furniture",
      "/meubels",
      "/mobel",
      "/lighting",
      "/verlichting",
      "/beleuchtung",
      "/art",
      "/kunst",
      "/decoration",
      "/decoratie",
      "/dekoration",
      "/tableware",
      "/tafelwaar",
      "/tafelgeschirr",
      "/coffee-table-books",
      "/koffietafelboeken",
      "/kaffeetisch-bucher",
    ],
    pageType: "plp",
  },
  {
    pathName: "/chat",
    pageType: "chat",
  },
  {
    /* WHO-6629: a Pro Buyer's client link (public, no Whoppah upsell), under every locale's word. */
    pathName: SELECTION_WORD_LIST.map(word => `/${word}/`),
    pageType: "pro_buyer_selection",
  },
  {
    pathName: "*",
    pageType: "search_results",
  },
] as const satisfies readonly PathNameToPageTypeMapEntry[];

/**
 * Every `page_type` the site reports, read off the map above rather than listed
 * again — so a page type can only be referred to by a name that exists.
 */
export type PageType = (typeof PATHNAME_PAGETYPE_MAP_ARRAY)[number]["pageType"];

/**
 * The page types that make up the checkout funnel: the cart, the delivery step,
 * the signup wall a guest meets between them, and the payment step (including
 * `/checkout2/complete`, where `purchase` fires).
 *
 * WHO-5689 stamps every event sent from these pages with the checkout's design
 * version, so the round that shipped is a dimension on the funnel rather than
 * something to be inferred from a deploy date.
 */
export const CHECKOUT_FUNNEL_PAGE_TYPES: readonly PageType[] = [
  "cart_signup",
  "cart_overview",
  "cart",
  "checkout",
];

/**
 * WHO-5689 round 1. Bump this when a later round changes the checkout again;
 * the value is what GA4 reports split on, so it must change with the design and
 * not with a release number.
 */
export const CHECKOUT_UI_VERSION = "round1";
